import {
  type LodgingIds,
  AvailabilityRequestEnum,
  LodgingCollection,
  type DateRange,
  type GuestsSelection,
  type Platform,
  type Location,
  type Initial,
  type More,
  type Suggestion,
  type AvailabilityRequest,
  type PaymentDue,
  type InitialSearch,
} from "@b2bportal/lodging-api";
import {
  AvailabilityResponseEnum,
  LodgingSelectionEnum,
} from "@b2bportal/lodging-api";
import { hotelsApi } from "@overrides/utilities";
import { merge } from "lodash-es";

interface AvailabilityQuery {
  stayDates: DateRange;
  guests: GuestsSelection;
  rooms: number;
  platform: Platform;
  paymentSchedule?: PaymentDue;
  request?: Partial<InitialSearch>;
}

export interface AvailabilityByPlaceQuery extends AvailabilityQuery {
  place: Suggestion;
}

export interface AvailabilityByLodgingQuery extends AvailabilityQuery {
  lodgingSelection: { LodgingSelection: "LodgingIds" } & LodgingIds;
}

export interface AvailabilityByLocationQuery extends AvailabilityQuery {
  location: Location;
}

export type AvailabilityApiCall =
  | { LodgingSelection: "Place"; request: AvailabilityByPlaceQuery }
  | { LodgingSelection: "LodgingIds"; request: AvailabilityByLodgingQuery }
  | { LodgingSelection: "Location"; request: AvailabilityByLocationQuery };

export const fetchAvailabilityByPlaceAPI = async (
  query: AvailabilityByPlaceQuery
): Promise<Initial> => {
  const placeId = query.place.id;
  if (
    placeId.Id !== "Lodgings" ||
    placeId.lodgingSelection.LodgingSelection !== "Place"
  ) {
    return Promise.reject(
      new Error("The server returned a next page availability request response")
    );
  }

  const body = merge(
    {
      progressiveConfig: {},
      excludeNonLuxuryLodgings: false,
      AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
      lodgingCollection: LodgingCollection.NoCollection,
      stayDates: query.stayDates,
      guests: query.guests,
      rooms: {
        numberOfRooms: query.rooms,
      },
      platform: query.platform,
    },
    { lodgingSelection: placeId.lodgingSelection },
    query.request ?? {}
  );
  return fetchInitialAvailability(body);
};

export const fetchAvailabilityNextPage = async (
  token: string
): Promise<More> => {
  const body = {
    progressiveConfig: {},
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.FollowUpSearch,
    moreToken: token,
    lodgingCollection: LodgingCollection.NoCollection,
  };
  try {
    const response = await hotelsApi.apiV0HotelsAvailabilityPost(body);

    if (response.data.AvailabilityResponse === AvailabilityResponseEnum.More) {
      return response.data;
    }

    throw new Error("The server returned a 'More' availability response");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    throw new Error(`Failed to fetch availability: ${errorMessage}`);
  }
};

export const fetchAvailabilityByLodgingId = async ({
  request,
  ...query
}: AvailabilityByLodgingQuery): Promise<Initial> => {
  const body = merge(
    {
      ...query,
      progressiveConfig: {},
      excludeNonLuxuryLodgings: false,
      AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
      lodgingCollection: LodgingCollection.NoCollection,
      rooms: { numberOfRooms: query.rooms },
    },
    request ?? {}
  );

  return fetchInitialAvailability(body);
};

export const fetchInitialAvailabilityByLocation = async (
  query: AvailabilityByLocationQuery
): Promise<Initial> => {
  const lodgingSelection = {
    LodgingSelection: LodgingSelectionEnum.Location,
    descriptor: query.location.descriptor,
    center: query.location.center,
  };
  const body = merge(
    {
      progressiveConfig: {},
      excludeNonLuxuryLodgings: false,
      AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
      lodgingCollection: LodgingCollection.NoCollection,
      lodgingSelection: lodgingSelection,
      stayDates: query.stayDates,
      guests: query.guests,
      rooms: { numberOfRooms: query.rooms },
      platform: query.platform,
    },
    query.request ?? {}
  );

  return fetchInitialAvailability(body);
};

export const fetchInitialAvailability = async (req: AvailabilityRequest) => {
  try {
    const response = await hotelsApi.apiV0HotelsAvailabilityPost(req);

    if (
      response.data.AvailabilityResponse === AvailabilityResponseEnum.Initial
    ) {
      return response.data;
    }

    throw new Error("The server returned a 'More' availability response");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    throw new Error(`Failed to fetch availability: ${errorMessage}`);
  }
};
